import { useEffect, useState } from 'react';
import { useStatusPolling } from './hooks/useStatusPolling';
import { fetchUptime, type UptimeJson } from './api/statusApi';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { OverallStatusBanner } from './components/OverallStatusBanner';
import { ComponentStatusList } from './components/ComponentStatusList';
import { IncidentTimeline } from './components/IncidentTimeline';
import { MaintenanceList } from './components/MaintenanceList';
import { SubscribeForm } from './components/SubscribeForm';
import './App.css';

export default function App() {
  const { data, isLoading, isStale } = useStatusPolling();
  const [uptime, setUptime] = useState<UptimeJson | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Uptime history changes at most once a day — fetch once on load rather than
    // on the 60s poll cycle, and fail quiet just like the index poller.
    fetchUptime()
      .then((json) => {
        if (!cancelled) setUptime(json);
      })
      .catch((err) => {
        console.warn('bloomz-status: /uptime.json fetch failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading && !data) {
    return (
      <div className="app">
        <Header />
        <main className="app__main container">
          <p className="app__loading">Loading current status…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app">
        <Header />
        <main className="app__main container">
          <p className="app__error">
            Unable to reach the status service right now. Please refresh, or check back shortly.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const openIncidents = data.active_incidents.filter((i) => i.status !== 'resolved');
  const resolvedIncidents = data.active_incidents.filter((i) => i.status === 'resolved');

  return (
    <div className="app">
      <Header />
      <OverallStatusBanner
        status={data.overall_status}
        generatedAt={data.generated_at}
        isStale={isStale}
        unmonitoredCount={data.components.filter((c) => c.monitored === false).length}
        componentCount={data.components.length}
      />
      <main className="app__main container">
        <div className="app__sections">
          <ComponentStatusList components={data.components} uptime={uptime} />
          <IncidentTimeline
            activeIncidents={openIncidents}
            recentResolved={resolvedIncidents}
            components={data.components}
          />
          <MaintenanceList windows={data.upcoming_maintenance} components={data.components} />
          <SubscribeForm />
        </div>
      </main>
      <Footer />
    </div>
  );
}
